// Setup › Memory (RECALL-ADMIN-MEMORY; design 2026-10-31 recall-full-integration §5.2, §7):
// owners and admins tend the company's memory in one place. Review is the Inbox's Memory lane in
// bulk; Browse reads a space (its map, records, a record page with versions, trace, links, chains
// and timeline) and flags, archives or restores a record; Spaces holds charter, agent access and
// who knows what; Rules the always-on list, fences, lifting and vetoes; Health what agents save,
// what waits and what to look at. What needs the memory service's admin act says "Waiting on
// memory service" (never an error) and is shown from what CompanyOS holds meanwhile. The owner's
// "See all agents' memory" switch is the lane's; personal spaces are never listed.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
  SettingsGroup,
  toast,
  ViewTabs,
  WorkerBadge,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowLeftIcon,
  FlagIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { SetupPage } from "../settings/setup-page";
import { WAITING, WaitingNote } from "./memory-admin-parts";
import { RulesTab, SpacesTab } from "./memory-admin-tend";
import { MemoryLane, SeeAllSwitch } from "./memory-lane";
import { QualityBlock } from "./memory-quality-block";
import {
  type AdminBrowse,
  type AdminHealth,
  type AdminJob,
  type AdminRecord,
  type AdminSpace,
  adminBrowseInfinite,
  adminHealthQuery,
  adminKeys,
  adminOverviewQuery,
  adminRecordQuery,
  type BrowseFilter,
} from "./memory-queries";

const TABS = ["review", "browse", "spaces", "rules", "health"] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = {
  review: "Review",
  browse: "Browse",
  spaces: "Spaces",
  rules: "Rules",
  health: "Health",
};

export function MemoryAdminScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const [tab, setTab] = useState<Tab>("browse");
  const overview = useQuery(adminOverviewQuery(api, company.id));
  const o = overview.data;
  return (
    <SetupPage title="Memory" width="full" slot="memory-admin">
      <div className="flex min-w-0 flex-col gap-4 px-6 py-5 max-md:px-4">
        <ViewTabs
          aria-label="Memory"
          value={tab}
          onValueChange={(v) => setTab(v as Tab)}
          tabs={TABS.map((t) => ({
            value: t,
            label: TAB_LABEL[t],
            ...(t === "review" && o && o.review_open > 0 ? { count: o.review_open } : {}),
          }))}
        />
        {o && !o.service_ready ? (
          <Notice tone="info" data-slot="admin-not-ready">
            {WAITING}: some tools below show what agents told memory until it opens them.
          </Notice>
        ) : null}
        {tab === "review" ? <ReviewTab api={api} company={company} /> : null}
        {tab === "browse" ? <BrowseTab api={api} company={company} /> : null}
        {tab === "spaces" ? <SpacesTab api={api} company={company} /> : null}
        {tab === "rules" ? <RulesTab api={api} company={company} /> : null}
        {tab === "health" ? <HealthTab api={api} company={company} /> : null}
      </div>
    </SetupPage>
  );
}

/** Say what filing a job did: filed, or why nothing was (waiting and nothing are not errors). */
function toldFiled(r: {
  result: "filed" | "waiting" | "nothing";
  note: string | null;
  job: AdminJob | null;
}) {
  if (r.result === "filed")
    toast.add({
      title: "Filed for the operator",
      ...(r.job?.pairs ? { description: `${r.job.pairs} conflicts` } : {}),
      type: "success",
    });
  else toast.add({ title: r.note ?? WAITING, type: "info" });
}

// ---------------------------------------------------------------------------------------------
// Review

function ReviewTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const [settling, setSettling] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="admin-review">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setSettling(true)}
          data-slot="admin-batch-settle"
        >
          Settle conflicts in bulk
        </Button>
        <InfoTip label="About settling in bulk">
          Conflicts are settled one by one below. This asks the operator to settle many at once in
          Recall, which only they can do. You see it under Health until it is done.
        </InfoTip>
      </div>
      <MemoryLane api={api} company={company} embedded />
      {settling ? (
        <BatchSettleDialog api={api} company={company} onClose={() => setSettling(false)} />
      ) : null}
    </div>
  );
}

function BatchSettleDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"keep_newer" | "dismiss">("keep_newer");
  const id = useId();
  const file = useMutation({
    mutationFn: () =>
      api.client.memory.adminJobFile({
        params: { co: company.id },
        body: { kind: "batch_settle", space: "all", mode },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      toldFiled(r);
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Settle conflicts in bulk"
      info="Applies to every open conflict you can see. The operator settles them in Recall; you see the job under Health."
      submitLabel="File it"
      pending={file.isPending}
      onSubmit={() => file.mutate()}
      onClose={onClose}
      slot="batch-settle-dialog"
      error={
        file.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(file.error, "Not filed. Try again.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>How</FieldLabel>
        <NativeSelect
          id={id}
          value={mode}
          onChange={(e) => setMode(e.target.value as "keep_newer" | "dismiss")}
        >
          <NativeSelectOption value="keep_newer">Keep the newer note</NativeSelectOption>
          <NativeSelectOption value="dismiss">Dismiss: not real conflicts</NativeSelectOption>
        </NativeSelect>
      </Field>
    </QuickAddDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Browse

const STATE_BADGE: Record<
  AdminRecord["state"],
  { label: string; variant: "warning" | "neutral" | "info" | "danger" } | null
> = {
  live: null,
  queued: { label: "Not saved yet", variant: "info" },
  proposed: { label: "Waiting for approval", variant: "warning" },
  flagged: { label: "Flagged", variant: "warning" },
  archived: { label: "Archived", variant: "neutral" },
};

function StateBadge({ state }: { state: AdminRecord["state"] }) {
  const b = STATE_BADGE[state];
  return b ? (
    <Badge variant={b.variant} className="h-5 text-[11px]" data-slot="record-state">
      {b.label}
    </Badge>
  ) : null;
}

function BrowseTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const [space, setSpace] = useState<string | undefined>(undefined);
  const [type, setType] = useState("");
  const [text, setText] = useState("");
  const [topic, setTopic] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);
  const f: BrowseFilter = {
    ...(space ? { space } : {}),
    ...(type ? { type } : {}),
    ...(text.trim() ? { q: text.trim() } : {}),
    ...(topic ? { topic } : {}),
  };
  const q = useInfiniteQuery({
    ...adminBrowseInfinite(api, company.id, f),
    placeholderData: (p) => p,
  });
  if (q.isPending) return <Loading label="Loading memory" rows={4} />;
  if (q.isError) return <LoadError what="Memory" error={q.error} />;
  const first = q.data.pages[0] as AdminBrowse;
  const d: AdminBrowse = { ...first, records: q.data.pages.flatMap((p) => p.records) };
  const types = [...new Set(d.spaces.flatMap((s) => Object.keys(s.by_type)))].sort();
  return (
    <div
      className="grid min-w-0 items-start gap-4 md:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[17rem_minmax(0,1fr)_minmax(0,26rem)]"
      data-slot="admin-browse"
    >
      <nav aria-label="Spaces" className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2 pb-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-faint">Spaces</span>
          {d.see_all.available ? (
            <span className="ml-auto">
              <SeeAllSwitch api={api} company={company} on={d.see_all.on} />
            </span>
          ) : null}
        </div>
        <SpaceButton
          label="All you can see"
          active={space === undefined}
          onClick={() => {
            setSpace(undefined);
            setTopic(undefined);
          }}
        />
        {d.spaces.map((s) => (
          <SpaceButton
            key={s.key}
            label={s.name}
            space={s}
            active={space === s.key}
            onClick={() => {
              setSpace(s.key);
              setTopic(undefined);
            }}
          />
        ))}
      </nav>
      <section className="flex min-w-0 flex-col gap-3" aria-label="Records">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <SearchIcon
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint"
            />
            <Input
              aria-label="Search records"
              placeholder="Search"
              className="pl-8"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <NativeSelect
            size="sm"
            aria-label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <NativeSelectOption value="">All types</NativeSelectOption>
            {types.map((t) => (
              <NativeSelectOption key={t} value={t}>
                {t}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {d.source === "companyos" ? <WaitingNote what="full map and history" /> : null}
        </div>
        {d.topics.length ? (
          <ul className="flex flex-wrap gap-1.5" aria-label="Map">
            {d.topics.map((t) => (
              <li key={t.topic}>
                <button
                  type="button"
                  aria-pressed={topic === t.topic}
                  onClick={() => setTopic(topic === t.topic ? undefined : t.topic)}
                  className="rounded-full border border-border px-2 py-0.5 text-xs aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
                >
                  {t.topic} <span className="text-faint tabular-nums">{t.count}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {d.unavailable ? <Notice tone="info">{d.unavailable}</Notice> : null}
        {d.records.length === 0 ? (
          <EmptyState
            variant="compact"
            title="No records here"
            description="Nothing has been saved to this space yet."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-subtle rounded-lg border border-border bg-card">
            {d.records.map((r) => (
              <RecordRow
                key={r.key}
                api={api}
                company={company}
                record={r}
                active={open === r.key}
                onOpen={() => setOpen(r.key)}
              />
            ))}
          </ul>
        )}
        {q.hasNextPage ? (
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
            data-slot="browse-more"
          >
            Show more
          </Button>
        ) : null}
      </section>
      {open ? (
        <RecordPage api={api} company={company} recordKey={open} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}

function SpaceButton({
  label,
  space,
  active,
  onClick,
}: {
  label: string;
  space?: AdminSpace;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? "true" : undefined}
      data-space={space?.key ?? "all"}
      onClick={onClick}
      className="flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-muted aria-[current=true]:bg-primary-soft aria-[current=true]:font-medium aria-[current=true]:text-primary-text"
    >
      {space?.agent ? (
        <WorkerBadge kind={space.agent.label === "DE" ? "de" : "ag"} size="sm" />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {space?.counted ? (
        <span className="text-xs text-faint tabular-nums">{space.records}</span>
      ) : null}
    </button>
  );
}

/** The hover icons on a record: flag, archive, restore. Visible on focus and on touch. */
function useRecordAct(api: Api, company: CompanyAccess) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; action: "flag" | "archive" | "restore" }) =>
      api.client.memory.adminRecordAct({ params: { co: company.id }, body: v }),
    onSuccess: (r, v) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      if (r.result === "done")
        toast.add({
          title: v.action === "flag" ? "Flagged" : v.action === "archive" ? "Archived" : "Restored",
          type: "success",
        });
      else toast.add({ title: r.note ?? WAITING, type: r.result === "waiting" ? "info" : "error" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not changed. Try again."), type: "error" }),
  });
}

function RecordActions({
  api,
  company,
  record,
}: {
  api: Api;
  company: CompanyAccess;
  record: Pick<AdminRecord, "key" | "title" | "state">;
}) {
  const act = useRecordAct(api, company);
  const icon =
    "size-7 shrink-0 opacity-0 group-hover/record:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100";
  const run = (action: "flag" | "archive" | "restore") => act.mutate({ key: record.key, action });
  return (
    <span className="flex items-center" data-slot="record-actions">
      {record.state === "archived" ? (
        <Button
          variant="ghost"
          size="icon-sm"
          className={icon}
          aria-label={`Restore ${record.title}`}
          title="Restore"
          disabled={act.isPending}
          onClick={() => run("restore")}
        >
          <ArchiveRestoreIcon aria-hidden="true" />
        </Button>
      ) : (
        <>
          {record.state !== "flagged" ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className={icon}
              aria-label={`Flag ${record.title}`}
              title="Flag"
              disabled={act.isPending}
              onClick={() => run("flag")}
            >
              <FlagIcon aria-hidden="true" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon-sm"
            className={icon}
            aria-label={`Archive ${record.title}`}
            title="Archive"
            disabled={act.isPending}
            onClick={() => run("archive")}
          >
            <ArchiveIcon aria-hidden="true" />
          </Button>
        </>
      )}
    </span>
  );
}

function RecordRow({
  api,
  company,
  record: r,
  active,
  onOpen,
}: {
  api: Api;
  company: CompanyAccess;
  record: AdminRecord;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <li
      className="group/record flex min-w-0 items-start gap-2 px-3 py-2 data-[active=true]:bg-primary-soft/40"
      data-slot="memory-record"
      data-record={r.key}
      data-state={r.state}
      data-active={active}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-left focus-visible:outline-2 focus-visible:outline-ring"
      >
        <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-foreground">
          <Badge variant="outline" className="h-5 shrink-0 text-[11px]">
            {r.type}
          </Badge>
          <span className="truncate">{r.title}</span>
          <StateBadge state={r.state} />
        </span>
        <span className="line-clamp-2 text-[13px] text-muted-foreground">{r.text}</span>
        <span className="text-xs text-faint">
          {r.space_name}
          {r.by ? ` · ${r.by}` : ""}
          {r.at ? ` · ${formatDateTime(r.at)}` : ""}
        </span>
      </button>
      <RecordActions api={api} company={company} record={r} />
    </li>
  );
}

function RecordPage({
  api,
  company,
  recordKey,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  recordKey: string;
  onClose: () => void;
}) {
  const q = useQuery(adminRecordQuery(api, company.id, recordKey));
  return (
    <aside
      aria-label="Record"
      data-slot="record-page"
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4 max-xl:md:col-span-2"
    >
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label="Close record" onClick={onClose}>
          <XIcon aria-hidden="true" className="max-xl:hidden" />
          <ArrowLeftIcon aria-hidden="true" className="xl:hidden" />
        </Button>
        {q.data ? <StateBadge state={q.data.record.state} /> : null}
      </div>
      {q.isPending ? (
        <Loading label="Loading record" rows={3} />
      ) : q.isError ? (
        <LoadError what="Record" error={q.error} />
      ) : (
        <RecordBody api={api} company={company} page={q.data} />
      )}
    </aside>
  );
}

function Section({
  title,
  empty,
  children,
}: {
  title: string;
  empty?: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">{title}</h3>
      {children ?? <p className="text-[13px] text-muted-foreground">{empty}</p>}
    </section>
  );
}

function RecordBody({
  api,
  company,
  page: p,
}: {
  api: Api;
  company: CompanyAccess;
  page: import("./memory-queries").AdminRecordPage;
}) {
  const r = p.record;
  const act = useRecordAct(api, company);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Badge variant="outline" className="h-5 text-[11px]">
            {r.type}
          </Badge>
          {r.space_name}
        </span>
        <h2 className="font-heading text-base font-semibold text-foreground">{r.title}</h2>
        <p className="whitespace-pre-wrap text-[13px] text-foreground" data-slot="record-text">
          {p.full_text}
        </p>
        {p.reason ? <p className="text-xs text-muted-foreground">Why: {p.reason}</p> : null}
        {p.evidence ? <p className="text-xs text-faint">Rests on {p.evidence}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-slot="record-page-actions">
        {r.state === "archived" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={!p.can_act || act.isPending}
            onClick={() => act.mutate({ key: r.key, action: "restore" })}
          >
            <ArchiveRestoreIcon aria-hidden="true" /> Restore
          </Button>
        ) : (
          <>
            {r.state !== "flagged" ? (
              <Button
                size="sm"
                variant="outline"
                disabled={!p.can_act || act.isPending}
                onClick={() => act.mutate({ key: r.key, action: "flag" })}
              >
                <FlagIcon aria-hidden="true" /> Flag
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              disabled={!p.can_act || act.isPending}
              onClick={() => act.mutate({ key: r.key, action: "archive" })}
            >
              <ArchiveIcon aria-hidden="true" /> Archive
            </Button>
          </>
        )}
        {!p.can_act ? (
          <span className="text-xs text-muted-foreground">Not saved to memory yet</span>
        ) : null}
        <InfoTip label="About flag and archive">
          Flag marks a record for a second look. Archive takes it out of what agents are briefed on;
          restore brings it back.
        </InfoTip>
      </div>
      {p.waiting_note ? <WaitingNote what="full history, links and chains" /> : null}
      <Section title="Versions">
        <ol className="flex flex-col gap-1.5" aria-label="Versions">
          {p.versions.map((v) => (
            <li key={v.n} className="text-[13px]">
              <span className="font-medium">v{v.n}</span>{" "}
              <span className="text-xs text-faint">
                {v.by ?? "Unknown"}
                {v.at ? ` · ${formatDateTime(v.at)}` : ""}
              </span>
              <p className="text-muted-foreground">{v.text}</p>
              {v.reason ? <p className="text-xs text-faint">{v.reason}</p> : null}
            </li>
          ))}
        </ol>
      </Section>
      <Section title="Trace">
        <ol className="flex flex-col gap-1" aria-label="Trace">
          {p.trace.map((t) => (
            <li key={`${t.kind}-${t.at}-${t.note}`} className="text-[13px]">
              <span className="font-medium capitalize">{t.kind}</span>
              <span className="text-xs text-faint">
                {t.by ? ` · ${t.by}` : ""}
                {t.at ? ` · ${formatDateTime(t.at)}` : ""}
              </span>
              {t.note ? <span className="text-muted-foreground"> {t.note}</span> : null}
            </li>
          ))}
        </ol>
      </Section>
      {p.expand.length ? (
        <Section title="Linked">
          <ul className="flex flex-col gap-1">
            {p.expand.map((l) => (
              <li key={`${l.type}-${l.title}-${l.edge}`} className="text-[13px]">
                <span className="text-xs text-faint">{l.edge}</span> {l.type}: {l.title}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {p.chains.length ? (
        <Section title="Chains">
          {p.chains.map((c) => (
            <p key={c.kind} className="text-[13px]">
              <span className="font-medium">{c.kind}</span>{" "}
              {c.steps.map((s) => s.title).join(" → ")}
            </p>
          ))}
        </Section>
      ) : null}
      {p.timeline.length ? (
        <Section title="Timeline">
          <ol className="flex flex-col gap-1">
            {p.timeline.map((t) => (
              <li key={`${t.type}-${t.title}-${t.at}`} className="text-[13px]">
                <span className="text-xs text-faint">{t.at ? formatDateTime(t.at) : ""}</span>{" "}
                {t.type}: {t.title}
              </li>
            ))}
          </ol>
        </Section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Health

function Stat({ label, value, info }: { label: string; value: number | string; info?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-border bg-card px-3 py-2">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info ? <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip> : null}
      </span>
      <span className="font-heading text-xl font-semibold tabular-nums">{value}</span>
    </div>
  );
}

const JOB_LABEL: Record<AdminJob["kind"], string> = {
  batch_settle: "Settle conflicts in bulk",
  clear_injection_flags: "Clear injection flags",
};
const JOB_BADGE: Record<AdminJob["state"], "warning" | "success" | "danger"> = {
  open: "warning",
  done: "success",
  failed: "danger",
};
const JOB_STATE: Record<AdminJob["state"], string> = {
  open: "With the operator",
  done: "Done",
  failed: "Failed",
};

const dash = (n: number | null) => (n === null ? "-" : n);

function HealthTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(adminHealthQuery(api, company.id));
  if (q.isPending) return <Loading label="Loading memory health" rows={4} />;
  if (q.isError) return <LoadError what="Memory health" error={q.error} />;
  const h: AdminHealth = q.data;
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="admin-health">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Saved"
          value={h.writes.sent}
          info="What agents told memory in the last 30 days."
        />
        <Stat
          label="Waiting to send"
          value={h.outbox.pending}
          info="Saves that have not reached the memory service yet."
        />
        <Stat label="To review" value={h.lane_open} info="Open cards in the Memory lane." />
        <Stat label="Conflicts" value={h.contradictions_open} />
      </div>
      <QualityBlock quality={h.quality} />
      {h.outbox.held > 0 ? (
        <Notice tone="warning">
          {h.outbox.held} saves are held back
          {h.outbox.by_reason.length
            ? `: ${h.outbox.by_reason.map((x) => `${x.reason} (${x.count})`).join(", ")}`
            : ""}
          .
        </Notice>
      ) : null}
      <SettingsGroup
        title="Spaces"
        actions={h.service_ready ? undefined : <WaitingNote what="stale, duplicates, injections" />}
      >
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-xs text-faint">
              <th className="px-4 py-1.5 font-medium">Space</th>
              <th className="px-2 py-1.5 text-right font-medium">Records</th>
              <th className="px-2 py-1.5 text-right font-medium">Stale</th>
              <th className="px-2 py-1.5 text-right font-medium">Duplicates</th>
              <th className="px-2 py-1.5 text-right font-medium">Never recalled</th>
              <th className="px-4 py-1.5 text-right font-medium">Injections</th>
            </tr>
          </thead>
          <tbody>
            {h.spaces.map((s) => (
              <tr key={s.key} className="border-t border-subtle" data-space-health={s.key}>
                <td className="px-4 py-1.5">{s.name}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{s.records}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{dash(s.stale)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{dash(s.duplicates)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{dash(s.never_recalled)}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{dash(s.injections)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SettingsGroup>
      {h.burden ? (
        <SettingsGroup title="Your review load">
          <p className="px-4 py-3 text-[13px]">
            {h.burden.waiting} waiting
            {h.burden.oldest_days !== null ? `, the oldest ${h.burden.oldest_days} days` : ""};{" "}
            {h.burden.answered_week} answered this week.
          </p>
        </SettingsGroup>
      ) : null}
      {h.inert_grants.length ? (
        <SettingsGroup title="Access that does nothing" count={h.inert_grants.length}>
          <ul className="flex flex-col px-4 py-1.5">
            {h.inert_grants.map((g) => (
              <li key={`${g.agent}-${g.space}`} className="py-1 text-[13px]">
                {g.agent} in {g.space}
                {g.reason ? <span className="text-muted-foreground"> · {g.reason}</span> : null}
              </li>
            ))}
          </ul>
        </SettingsGroup>
      ) : null}
      <InjectionFlags api={api} company={company} health={h} />
      <SettingsGroup
        title="With the operator"
        count={h.jobs.filter((j) => j.state === "open").length}
      >
        {h.jobs.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-muted-foreground">Nothing filed.</p>
        ) : (
          <ul className="flex flex-col px-4 py-1.5" data-slot="admin-jobs">
            {h.jobs.map((j) => (
              <li key={j.id} className="flex flex-wrap items-center gap-2 py-1.5 text-[13px]">
                <span className="font-medium">{JOB_LABEL[j.kind]}</span>
                <span className="text-muted-foreground">{j.space_name ?? j.space_key}</span>
                {j.pairs !== null ? (
                  <span className="text-xs text-faint">{j.pairs} conflicts</span>
                ) : null}
                <Badge variant={JOB_BADGE[j.state]} className="h-5 text-[11px]">
                  {JOB_STATE[j.state]}
                </Badge>
                <span className="ml-auto text-xs text-faint">{formatDateTime(j.filed_at)}</span>
                {j.note ? (
                  <span className="basis-full text-xs text-muted-foreground">{j.note}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </SettingsGroup>
    </div>
  );
}

function InjectionFlags({
  api,
  company,
  health: h,
}: {
  api: Api;
  company: CompanyAccess;
  health: AdminHealth;
}) {
  const qc = useQueryClient();
  const clear = useMutation({
    mutationFn: (space: string) =>
      api.client.memory.adminJobFile({
        params: { co: company.id },
        body: { kind: "clear_injection_flags", space },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all(company.id) });
      toldFiled(r);
    },
    onError: (e) => toast.add({ title: problemText(e, "Not filed. Try again."), type: "error" }),
  });
  const spaces = [...new Map(h.injection_flags.map((f) => [f.space_key, f.space_name])).entries()];
  return (
    <SettingsGroup
      title="Looks like an instruction"
      count={h.injection_flags.length}
      actions={
        <InfoTip label="About injection flags">
          Notes that read like orders to an agent are held back from briefings. Clearing a flag is
          done by the operator in Recall; file it here.
        </InfoTip>
      }
    >
      {h.injection_flags.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">
          {h.service_ready ? "Nothing flagged." : WAITING}
        </p>
      ) : (
        <ul className="flex flex-col px-4 py-1.5">
          {h.injection_flags.map((f) => (
            <li key={`${f.space_key}-${f.type}-${f.title}`} className="py-1 text-[13px]">
              {f.title}
              <span className="text-xs text-faint"> · {f.space_name}</span>
              {f.reason ? <span className="text-muted-foreground"> · {f.reason}</span> : null}
            </li>
          ))}
          <li className="flex flex-wrap gap-2 py-1.5">
            {spaces.map(([key, name]) => (
              <Button
                key={key}
                size="sm"
                variant="outline"
                disabled={clear.isPending}
                onClick={() => clear.mutate(key)}
              >
                Clear flags in {name}
              </Button>
            ))}
          </li>
        </ul>
      )}
    </SettingsGroup>
  );
}
